// The team the publisher wants to have access, split into sales reps and
// managers. Modeled on the internal form's markets repeater.
//
// These become pre-authorization claims at finalize: when one of these people
// signs up with the email listed here, they land with the right role and
// station scope automatically.

import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { inputClass, labelClass } from '../admin/fields/publisherFieldStyles';
import type { IntakeTeamMember, IntakeTeamRole } from '../../../types/publisherIntake';

const ROLE_LABEL: Record<IntakeTeamRole, string> = {
  sales_rep: 'Sales rep',
  manager: 'Manager',
};

interface Props {
  members: IntakeTeamMember[];
  onChange: (next: IntakeTeamMember[]) => void;
  /** Keyed `teamMembers.<index>.<field>` plus a top-level `teamMembers`. */
  errors?: Partial<Record<string, string>>;
}

const TeamMembersFields: React.FC<Props> = ({ members, onChange, errors = {} }) => {
  const update = (i: number, patch: Partial<IntakeTeamMember>) =>
    onChange(members.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  const remove = (i: number) => onChange(members.filter((_, idx) => idx !== i));

  const add = () =>
    onChange([...members, { name: '', email: '', role: 'sales_rep' }]);

  return (
    <div className="space-y-4">
      {members.length === 0 && (
        <p className="text-[12px] text-slate-500">
          Add the people who should be able to manage campaigns for your channel.
        </p>
      )}

      {members.map((m, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-start">
          <div className="col-span-12 sm:col-span-4">
            <label className={labelClass}>Name</label>
            <input
              value={m.name}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Dana Whitfield"
              className={inputClass}
            />
            {errors[`teamMembers.${i}.name`] && (
              <p className="mt-1 text-[11px] text-rose-600">{errors[`teamMembers.${i}.name`]}</p>
            )}
          </div>
          <div className="col-span-12 sm:col-span-4">
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={m.email}
              onChange={(e) => update(i, { email: e.target.value })}
              placeholder="dana@yourstation.com"
              className={inputClass}
            />
            {errors[`teamMembers.${i}.email`] && (
              <p className="mt-1 text-[11px] text-rose-600">{errors[`teamMembers.${i}.email`]}</p>
            )}
          </div>
          <div className="col-span-10 sm:col-span-3">
            <label className={labelClass}>Role</label>
            <select
              value={m.role}
              onChange={(e) => update(i, { role: e.target.value as IntakeTeamRole })}
              className={inputClass}
              aria-label={`Role for team member ${i + 1}`}
            >
              {(Object.keys(ROLE_LABEL) as IntakeTeamRole[]).map((role) => (
                <option key={role} value={role}>{ROLE_LABEL[role]}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1 flex justify-end pt-6">
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-2.5 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              aria-label={`Remove team member ${i + 1}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-slate-900 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Add team member
      </button>

      {errors.teamMembers && (
        <p className="text-[12px] text-rose-600">{errors.teamMembers}</p>
      )}

      <p className="text-[11px] text-slate-500 leading-relaxed">
        Managers can see referral and commission reporting; sales reps get a referral code
        and campaign access. Everyone listed here gets access automatically the first time
        they sign in with the email above.
      </p>
    </div>
  );
};

export default TeamMembersFields;
